import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PageHeader from '../../components/common/PageHeader';
import TableSkeleton from '../../components/common/TableSkeleton';
import TipTapEditor from '../../components/common/TipTapEditor';
import { sandboxService } from '../../services/sandboxService';
import { SharedAuditDetail } from '../../types';
import { extractApiError } from '../../utils/errorUtils';
import { formatSeverityLower, getKritikalitaetChipSx } from '../../utils/severity';

/** Nur-Lesen-Ansicht eines freigegebenen Audits (eingeschraenkte Sitzung). */
const SharedAuditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [audit, setAudit] = useState<SharedAuditDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    sandboxService.shared
      .audit(parseInt(id, 10))
      .then((a) => {
        setAudit(a);
        setError(null);
      })
      .catch((e) => setError(extractApiError(e, 'Audit nicht freigegeben')))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <TableSkeleton />;
  if (error || !audit) {
    return (
      <Box>
        <PageHeader title="Freigegebenes Audit" backTo="/sandbox" />
        <Alert severity="error">{error || 'Audit nicht freigegeben'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title={audit.name} subtitle="Freigegebenes Audit – nur lesen" backTo="/sandbox" />
      <Alert icon={<LockIcon fontSize="inherit" />} severity="warning" sx={{ mb: 2 }}>
        Nur lesen: Dieses Audit wurde vom Hauptaccount zum Lesen freigegeben. Änderungen sind hier nicht möglich.
      </Alert>

      {audit.beschreibung && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TipTapEditor label="Beschreibung" content={audit.beschreibung} editable={false} minHeight={60} />
          </CardContent>
        </Card>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
        {audit.findings.length} Findings
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Titel</TableCell>
              <TableCell>Kritikalität</TableCell>
              <TableCell>CVSS</TableCell>
              <TableCell>Host</TableCell>
              <TableCell>Klasse</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audit.findings.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Keine Findings</Typography>
                </TableCell>
              </TableRow>
            )}
            {audit.findings.map((f) => (
              <TableRow key={f.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sandbox/shared/findings/${f.id}`)}>
                <TableCell sx={{ fontWeight: 600 }}>{f.titel}</TableCell>
                <TableCell>
                  {f.kritikalitaet ? <Chip size="small" label={formatSeverityLower(f.kritikalitaet)} sx={getKritikalitaetChipSx(f.kritikalitaet)} /> : '–'}
                </TableCell>
                <TableCell>{f.cvss31_score != null ? f.cvss31_score.toFixed(1) : '–'}</TableCell>
                <TableCell>{f.host || '–'}</TableCell>
                <TableCell>{f.schwachstellenklasse?.name_de || '–'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SharedAuditPage;
